import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EventModel } from '@app/events/shared/event.model';
import {
  EventPlannerEntryConfig,
  EventPlannerEntryInputType,
  EventPlannerEntryModel,
  EventPlannerModel,
  EventPlannerSectionModel,
} from '@app/events/shared/event-planner.model';
import { EventPlannerService } from '@app/events/shared/event-planner.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { ToastService } from '@app/shared/toast/toast.service';
import { CircleHelp, FileText, LucideAngularModule, Plus } from 'lucide-angular';

@Component({
  selector: 'app-event-planner',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    LucideAngularModule,
    ButtonComponent,
    ModalComponent,
    SelectComponent,
    TextInputComponent,
    TextareaComponent,
  ],
  templateUrl: './planner.component.html',
  styleUrls: [],
})
export class EventPlannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly SCROLL_OFFSET = 24;
  private static readonly BOTTOM_TOLERANCE = 8;
  private static readonly MANUAL_NAV_TIMEOUT_MS = 1500;
  private static readonly ACTIVATION_LINE_RATIO = 0.35;

  @Input({ required: true }) public event!: EventModel;
  @Output() public dirtyStateChange = new EventEmitter<boolean>();

  protected readonly CircleHelpIcon = CircleHelp;
  protected readonly FileTextIcon = FileText;
  protected readonly PlusIcon = Plus;

  protected planner: EventPlannerModel | null = null;
  protected plannerSections: EventPlannerSectionModel[] = [];
  protected plannerForm = new FormGroup({
    sections: new FormArray<FormGroup>([]),
  });
  protected activeSectionKey: string | null = null;
  protected loading = false;
  protected saving = false;
  protected loadError: string | null = null;
  protected isAddEntryModalOpen = false;
  protected addEntrySectionIndex: number | null = null;
  protected addEntryKind: 'question' | 'note' | null = null;
  protected addEntryForm = new FormGroup({
    label: new FormControl(''),
    answer_text: new FormControl(''),
  });

  private scrollContainer: HTMLElement | null = null;
  private manualScrollTargetKey: string | null = null;
  private manualScrollResetTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly handleViewportChange = () => this.zone.run(() => this.updateActiveSection());

  constructor(
    private plannerService: EventPlannerService,
    private toastService: ToastService,
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.plannerForm.valueChanges.subscribe(() => {
      this.emitDirtyState();
    });

    this.loadPlanner();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.initializeScrollTracking());
  }

  ngOnDestroy(): void {
    this.detachScrollTracking();
    this.clearManualScrollTarget();
  }

  protected loadPlanner(): void {
    if (!this.event?.id) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    this.plannerService.getPlanner(this.event.id).subscribe({
      next: (response) => {
        this.setPlanner(response.data);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading planner:', error);
        this.loadError = 'Failed to load event planner.';
        this.loading = false;
      },
    });
  }

  protected savePlanner(): void {
    if (!this.event?.id || !this.planner || this.saving) {
      return;
    }

    this.saving = true;

    this.plannerService.updatePlanner(this.event.id, this.serializeSections()).subscribe({
      next: (response) => {
        this.setPlanner(response.data);
        this.saving = false;
        this.toastService.success('Event planner saved successfully');
      },
      error: (error) => {
        console.error('Error saving planner:', error);
        this.saving = false;
        this.toastService.error('Failed to save event planner');
      },
    });
  }

  protected addContact(sectionIndex: number): void {
    const section = this.plannerSections[sectionIndex];

    if (!section) {
      return;
    }

    const entry: EventPlannerEntryModel = {
      id: null,
      key: null,
      label: `Contact ${section.entries.filter((item) => item.input_type === 'contact_row').length + 1}`,
      input_type: 'contact_row',
      config: this.getDefaultContactConfig(section),
      answer_text: null,
      answer_json: {
        role: null,
        details: null,
      },
      is_custom: true,
      is_note: false,
      sort_order: section.entries.length,
    };

    section.entries.push(entry);
    this.entriesArray(sectionIndex).push(this.createEntryGroup(entry));
    this.reindexSectionEntries(sectionIndex);
    this.plannerForm.markAsDirty();
    this.emitDirtyState();
  }

  protected addCustomQuestion(sectionIndex: number): void {
    this.openAddEntryModal(sectionIndex, 'question');
  }

  protected addCustomNote(sectionIndex: number): void {
    this.openAddEntryModal(sectionIndex, 'note');
  }

  protected removeEntry(sectionIndex: number, entryIndex: number): void {
    const section = this.plannerSections[sectionIndex];

    if (!section) {
      return;
    }

    section.entries.splice(entryIndex, 1);
    this.entriesArray(sectionIndex).removeAt(entryIndex);
    this.reindexSectionEntries(sectionIndex);
    this.plannerForm.markAsDirty();
    this.emitDirtyState();
  }

  protected canRemoveEntry(entry: EventPlannerEntryModel): boolean {
    return entry.is_custom;
  }

  protected isTextEntry(entry: EventPlannerEntryModel): boolean {
    return entry.input_type === 'text';
  }

  protected isTextareaEntry(entry: EventPlannerEntryModel): boolean {
    return entry.input_type === 'textarea';
  }

  protected isSelectEntry(entry: EventPlannerEntryModel): boolean {
    return entry.input_type === 'select';
  }

  protected isContactEntry(entry: EventPlannerEntryModel): boolean {
    return entry.input_type === 'contact_row';
  }

  protected getSelectChoices(entry: EventPlannerEntryModel): { value: string; label: string }[] {
    return (entry.config?.options ?? []).map((option) => ({
      value: option,
      label: option,
    }));
  }

  protected getContactRoleChoices(entry: EventPlannerEntryModel): { value: string; label: string }[] {
    return (entry.config?.role_options ?? []).map((option) => ({
      value: option,
      label: option,
    }));
  }

  protected getContactDetailsLabel(entry: EventPlannerEntryModel): string {
    const label = entry.config?.details_label?.trim();

    return !label || label === 'Point of Contact' ? 'Name' : label;
  }

  protected contactEntries(sectionIndex: number): { entry: EventPlannerEntryModel; index: number }[] {
    return this.plannerSections[sectionIndex]?.entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => entry.input_type === 'contact_row') ?? [];
  }

  protected nonContactEntries(sectionIndex: number): { entry: EventPlannerEntryModel; index: number }[] {
    return this.plannerSections[sectionIndex]?.entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => entry.input_type !== 'contact_row') ?? [];
  }

  protected preContactEntries(sectionIndex: number): { entry: EventPlannerEntryModel; index: number }[] {
    const entries = this.plannerSections[sectionIndex]?.entries ?? [];
    const firstContactIndex = entries.findIndex((entry) => entry.input_type === 'contact_row');

    if (firstContactIndex === -1) {
      return [];
    }

    return entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry, index }) => entry.input_type !== 'contact_row' && index < firstContactIndex);
  }

  protected contactsCustomEntries(sectionIndex: number): { entry: EventPlannerEntryModel; index: number }[] {
    const entries = this.plannerSections[sectionIndex]?.entries ?? [];
    const firstContactIndex = entries.findIndex((entry) => entry.input_type === 'contact_row');

    return entries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry, index }) => {
        if (entry.input_type === 'contact_row') {
          return false;
        }

        if (entry.key === 'contacts_notes') {
          return false;
        }

        return firstContactIndex === -1 || index > firstContactIndex;
      });
  }

  protected contactsNotesEntry(sectionIndex: number): { entry: EventPlannerEntryModel; index: number } | null {
    const entries = this.plannerSections[sectionIndex]?.entries ?? [];
    const index = entries.findIndex((entry) => entry.key === 'contacts_notes');

    if (index === -1) {
      return null;
    }

    return { entry: entries[index], index };
  }

  protected getHint(entry: EventPlannerEntryModel): string {
    return entry.config?.hint ?? '';
  }

  protected getPlaceholder(entry: EventPlannerEntryModel): string {
    return entry.config?.placeholder ?? '';
  }

  protected getContactDetailsPlaceholder(entry: EventPlannerEntryModel): string {
    const placeholder = entry.config?.details_placeholder?.trim();

    return !placeholder || placeholder === 'Name and contact details' ? 'Name' : placeholder;
  }

  protected entryAnswerTextControl(sectionIndex: number, entryIndex: number): FormControl {
    return this.entriesArray(sectionIndex).at(entryIndex).get('answer_text') as FormControl;
  }

  protected contactRoleControl(sectionIndex: number, entryIndex: number): FormControl {
    return this.contactAnswerGroup(sectionIndex, entryIndex).get('role') as FormControl;
  }

  protected contactDetailsControl(sectionIndex: number, entryIndex: number): FormControl {
    return this.contactAnswerGroup(sectionIndex, entryIndex).get('details') as FormControl;
  }

  protected sectionAnchorId(section: EventPlannerSectionModel): string {
    return `planner-section-${section.key}`;
  }

  protected scrollToSection(section: EventPlannerSectionModel): void {
    const sectionId = this.sectionAnchorId(section);
    const element = document.getElementById(sectionId);

    if (!element) {
      return;
    }

    this.setManualScrollTarget(section.key);
    this.scrollContainer ??= this.findScrollContainer(element);

    if (this.scrollContainer) {
      const containerRect = this.scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const nextTop = this.scrollContainer.scrollTop + (elementRect.top - containerRect.top) - EventPlannerComponent.SCROLL_OFFSET;

      this.scrollContainer.scrollTo({
        top: Math.max(0, nextTop),
        behavior: 'smooth',
      });

      return;
    }

    const nextTop = window.scrollY + element.getBoundingClientRect().top - EventPlannerComponent.SCROLL_OFFSET;
    window.scrollTo({
      top: Math.max(0, nextTop),
      behavior: 'smooth',
    });
  }

  protected trackSection(index: number, section: EventPlannerSectionModel): number | string {
    return section.id ?? section.key ?? index;
  }

  protected trackEntry(index: number, entry: EventPlannerEntryModel): number | string {
    return entry.id ?? entry.key ?? index;
  }

  protected closeAddEntryModal(): void {
    this.isAddEntryModalOpen = false;
    this.addEntrySectionIndex = null;
    this.addEntryKind = null;
    this.addEntryForm.reset({
      label: '',
      answer_text: '',
    });
    this.addEntryForm.markAsPristine();
    this.addEntryForm.markAsUntouched();
  }

  protected confirmAddEntry(): void {
    const label = this.addEntryForm.controls.label.value?.trim();
    const answerText = this.addEntryForm.controls.answer_text.value?.trim() ?? '';

    if (!label || this.addEntrySectionIndex === null || !this.addEntryKind) {
      return;
    }

    this.addCustomEntry(this.addEntrySectionIndex, {
      label,
      input_type: this.addEntryKind === 'note' ? 'textarea' : 'text',
      is_note: this.addEntryKind === 'note',
      answer_text: answerText,
    });

    this.closeAddEntryModal();
  }

  protected get addEntryModalTitle(): string {
    return this.addEntryKind === 'note' ? 'Add Note' : 'Add Question';
  }

  protected get addEntryConfirmLabel(): string {
    return this.addEntryKind === 'note' ? 'Add Note' : 'Add Question';
  }

  protected get addEntryPrimaryFieldLabel(): string {
    return 'Title';
  }

  protected get addEntryPrimaryFieldPlaceholder(): string {
    return this.addEntryKind === 'note' ? '' : 'Question title';
  }

  protected get addEntryValueFieldLabel(): string {
    return 'Content';
  }

  private setPlanner(planner: EventPlannerModel): void {
    this.planner = this.clonePlanner(planner);
    this.plannerSections = this.planner.sections;
    this.plannerForm.setControl(
      'sections',
      new FormArray<FormGroup>(this.plannerSections.map((section) => this.createSectionGroup(section))),
    );
    this.plannerForm.markAsPristine();
    this.clearManualScrollTarget();
    this.activeSectionKey = this.plannerSections[0]?.key ?? null;
    this.emitDirtyState();
    requestAnimationFrame(() => this.initializeScrollTracking());
  }

  private createSectionGroup(section: EventPlannerSectionModel): FormGroup {
    return new FormGroup({
      id: new FormControl(section.id),
      key: new FormControl(section.key),
      title: new FormControl(section.title),
      section_type: new FormControl(section.section_type),
      intro_text: new FormControl(section.intro_text),
      sort_order: new FormControl(section.sort_order),
      entries: new FormArray<FormGroup>(section.entries.map((entry) => this.createEntryGroup(entry))),
    });
  }

  private createEntryGroup(entry: EventPlannerEntryModel): FormGroup {
    return new FormGroup({
      id: new FormControl(entry.id),
      key: new FormControl(entry.key),
      label: new FormControl(entry.label),
      input_type: new FormControl(entry.input_type),
      config: new FormControl(entry.config ?? {}),
      answer_text: new FormControl(entry.answer_text ?? ''),
      answer_json: new FormGroup({
        role: new FormControl(entry.answer_json?.role ?? null),
        details: new FormControl(entry.answer_json?.details ?? ''),
      }),
      is_custom: new FormControl(entry.is_custom),
      is_note: new FormControl(entry.is_note),
      sort_order: new FormControl(entry.sort_order),
    });
  }

  private serializeSections(): EventPlannerSectionModel[] {
    const sections = this.sectionGroups.getRawValue();

    return sections.map((section: any, sectionIndex: number) => ({
      id: section.id,
      key: section.key,
      title: section.title,
      section_type: section.section_type,
      intro_text: section.intro_text,
      sort_order: sectionIndex,
      entries: (section.entries ?? []).map((entry: any, entryIndex: number) => ({
        id: entry.id,
        key: entry.key,
        label: entry.label,
        input_type: entry.input_type as EventPlannerEntryInputType,
        config: entry.config ?? {},
        answer_text: this.usesTextInputType(entry.input_type) ? (entry.answer_text ?? '') : null,
        answer_json: entry.input_type === 'contact_row'
          ? {
              role: entry.answer_json?.role ?? null,
              details: entry.answer_json?.details ?? null,
            }
          : null,
        is_custom: !!entry.is_custom,
        is_note: !!entry.is_note,
        sort_order: entryIndex,
      })),
    }));
  }

  private addCustomEntry(
    sectionIndex: number,
    definition: { label: string; input_type: EventPlannerEntryInputType; is_note: boolean; answer_text: string },
  ): void {
    const section = this.plannerSections[sectionIndex];

    if (!section) {
      return;
    }

    const entry: EventPlannerEntryModel = {
      id: null,
      key: null,
      label: definition.label,
      input_type: definition.input_type,
      config: {},
      answer_text: definition.answer_text,
      answer_json: null,
      is_custom: true,
      is_note: definition.is_note,
      sort_order: section.entries.length,
    };

    section.entries.push(entry);
    this.entriesArray(sectionIndex).push(this.createEntryGroup(entry));
    this.reindexSectionEntries(sectionIndex);
    this.plannerForm.markAsDirty();
    this.emitDirtyState();
  }

  private openAddEntryModal(sectionIndex: number, kind: 'question' | 'note'): void {
    this.addEntrySectionIndex = sectionIndex;
    this.addEntryKind = kind;
    this.isAddEntryModalOpen = true;
    this.addEntryForm.reset({
      label: '',
      answer_text: '',
    });
    this.addEntryForm.markAsPristine();
    this.addEntryForm.markAsUntouched();
  }

  private get sectionGroups(): FormArray<FormGroup> {
    return this.plannerForm.controls.sections;
  }

  private entriesArray(sectionIndex: number): FormArray<FormGroup> {
    return this.sectionGroups.at(sectionIndex).get('entries') as FormArray<FormGroup>;
  }

  private contactAnswerGroup(sectionIndex: number, entryIndex: number): FormGroup {
    return this.entriesArray(sectionIndex).at(entryIndex).get('answer_json') as FormGroup;
  }

  private clonePlanner(planner: EventPlannerModel): EventPlannerModel {
    return JSON.parse(JSON.stringify(planner)) as EventPlannerModel;
  }

  private usesTextInputType(inputType: EventPlannerEntryInputType): boolean {
    return inputType === 'text'
      || inputType === 'textarea'
      || inputType === 'select';
  }

  private getDefaultContactConfig(section: EventPlannerSectionModel): EventPlannerEntryConfig {
    return section.entries.find((entry) => entry.input_type === 'contact_row')?.config ?? {
      role_options: [],
      details_label: 'Name',
      details_placeholder: 'Name',
    };
  }

  private reindexSectionEntries(sectionIndex: number): void {
    const section = this.plannerSections[sectionIndex];
    const entryForms = this.entriesArray(sectionIndex);
    let contactNumber = 1;

    section.entries.forEach((entry, index) => {
      entry.sort_order = index;

      if (entry.input_type === 'contact_row') {
        entry.label = `Contact ${contactNumber}`;
        contactNumber++;
      }

      const entryForm = entryForms.at(index);
      entryForm.get('sort_order')?.setValue(index, { emitEvent: false });

      if (entry.input_type === 'contact_row') {
        entryForm.get('label')?.setValue(entry.label, { emitEvent: false });
      }
    });
  }

  private emitDirtyState(): void {
    this.dirtyStateChange.emit(this.plannerForm.dirty);
  }

  private initializeScrollTracking(): void {
    this.detachScrollTracking();

    const firstSection = this.plannerSections[0]
      ? document.getElementById(this.sectionAnchorId(this.plannerSections[0]))
      : null;

    if (firstSection) {
      this.scrollContainer = this.findScrollContainer(firstSection);
      this.scrollContainer?.addEventListener('scroll', this.handleViewportChange, { passive: true });
    }

    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    this.updateActiveSection();
  }

  private detachScrollTracking(): void {
    this.scrollContainer?.removeEventListener('scroll', this.handleViewportChange);
    this.scrollContainer = null;
    window.removeEventListener('resize', this.handleViewportChange);
  }

  private updateActiveSection(): void {
    if (!this.plannerSections.length) {
      this.activeSectionKey = null;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const viewportTop = this.scrollContainer?.getBoundingClientRect().top ?? 0;
    const viewportBottom = this.scrollContainer?.getBoundingClientRect().bottom ?? window.innerHeight;
    const activationLine = viewportTop + ((viewportBottom - viewportTop) * EventPlannerComponent.ACTIVATION_LINE_RATIO);
    const lastSectionKey = this.plannerSections[this.plannerSections.length - 1]?.key ?? null;
    const isAtBottom = !!this.scrollContainer
      && (this.scrollContainer.scrollTop + this.scrollContainer.clientHeight >= this.scrollContainer.scrollHeight - EventPlannerComponent.BOTTOM_TOLERANCE);

    if (this.manualScrollTargetKey) {
      if (this.isManualTargetReached(this.manualScrollTargetKey, isAtBottom, viewportTop, viewportBottom)) {
        this.clearManualScrollTarget();
      } else {
        this.activeSectionKey = this.manualScrollTargetKey;
        this.changeDetectorRef.detectChanges();
        return;
      }
    }

    if (isAtBottom) {
      this.activeSectionKey = lastSectionKey;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const measuredSections = this.plannerSections
      .map((section) => {
        const element = document.getElementById(this.sectionAnchorId(section));

        if (!element) {
          return null;
        }

        const rect = element.getBoundingClientRect();

        return {
          section,
          top: rect.top,
          bottom: rect.bottom,
          distanceToActivationLine: Math.abs(rect.top - activationLine),
        };
      })
      .filter((item): item is { section: EventPlannerSectionModel; top: number; bottom: number; distanceToActivationLine: number } => !!item);

    if (!measuredSections.length) {
      this.activeSectionKey = null;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const crossedSections = measuredSections.filter((item) => item.top <= activationLine);

    if (crossedSections.length) {
      this.activeSectionKey = crossedSections[crossedSections.length - 1].section.key;
      this.changeDetectorRef.detectChanges();
      return;
    }

    const closestSection = measuredSections.sort((first, second) => first.distanceToActivationLine - second.distanceToActivationLine)[0];
    this.activeSectionKey = closestSection.section.key;
    this.changeDetectorRef.detectChanges();
  }

  private findScrollContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement;

    while (current) {
      const styles = window.getComputedStyle(current);
      const overflowY = styles.overflowY;

      if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private setManualScrollTarget(sectionKey: string): void {
    this.manualScrollTargetKey = sectionKey;
    this.activeSectionKey = sectionKey;

    if (this.manualScrollResetTimer) {
      clearTimeout(this.manualScrollResetTimer);
    }

    this.manualScrollResetTimer = setTimeout(() => {
      this.manualScrollTargetKey = null;
      this.manualScrollResetTimer = null;
      this.updateActiveSection();
    }, EventPlannerComponent.MANUAL_NAV_TIMEOUT_MS);
  }

  private clearManualScrollTarget(): void {
    this.manualScrollTargetKey = null;

    if (this.manualScrollResetTimer) {
      clearTimeout(this.manualScrollResetTimer);
      this.manualScrollResetTimer = null;
    }
  }

  private isManualTargetReached(sectionKey: string, isAtBottom: boolean, viewportTop: number, viewportBottom: number): boolean {
    if (isAtBottom && sectionKey === this.plannerSections[this.plannerSections.length - 1]?.key) {
      return true;
    }

    const element = document.getElementById(`planner-section-${sectionKey}`);

    if (!element) {
      return true;
    }

    const rect = element.getBoundingClientRect();
    const visibleTop = Math.max(rect.top, viewportTop);
    const visibleBottom = Math.min(rect.bottom, viewportBottom);

    return visibleBottom - visibleTop >= Math.min(rect.height, 120);
  }
}
